import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderId, setOrderId] = useState(null);
  const { getAuthHeader } = useAuth();
  const { fetchCart } = useCart();

  const sessionId = searchParams.get('session_id');
  const paypalPaymentId = searchParams.get('paymentId');
  const paypalPayerId = searchParams.get('PayerID');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else if (paypalPaymentId && paypalPayerId) {
      executePayPal();
    } else {
      setStatus('error');
    }
  }, [sessionId, paypalPaymentId, paypalPayerId]);

  const executePayPal = async () => {
    try {
      const response = await axios.post(
        `${API}/paypal/execute`,
        { payment_id: paypalPaymentId, payer_id: paypalPayerId },
        { headers: getAuthHeader() }
      );
      setOrderId(response.data.order_id);
      setStatus('success');
      fetchCart();
    } catch (error) {
      console.error('PayPal execute error:', error);
      setStatus('error');
    }
  };

  const pollPaymentStatus = async (attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: getAuthHeader()
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setOrderId(response.data.order_id);
        fetchCart(); // Refresh cart
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="checkout-success-page">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-mrc-navy animate-spin mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-mrc-navy mb-2">
            Ap verifye peman...
          </h1>
          <p className="text-muted-foreground">
            Tann yon moman silvouplè
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 py-16" data-testid="checkout-success-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white p-8 md:p-12 shadow-lg">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-mrc-navy mb-4">
              Mèsi pou kòmand ou!
            </h1>
            <p className="text-muted-foreground mb-8">
              Peman ou te reyisi. Nou pral prepare kòmand ou touswit.
            </p>

            {orderId && (
              <div className="bg-gray-50 p-4 mb-8">
                <p className="text-sm text-muted-foreground mb-1">Nimewo Kòmand</p>
                <p className="font-mono text-lg font-bold text-mrc-navy" data-testid="order-id">
                  {orderId}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders">
                <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8" data-testid="view-orders-btn">
                  <Package className="mr-2 h-4 w-4" />
                  Wè Kòmand yo
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="px-8">
                  Kontinye Achte
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-screen bg-gray-50 py-16" data-testid="checkout-success-page">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <div className="bg-white p-8 md:p-12 shadow-lg">
          <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="h-10 w-10 text-red-600" />
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-mrc-navy mb-4" data-testid="checkout-status-title">
            {status === 'expired' ? 'Sesyon ekspire' : status === 'timeout' ? 'Peman an poko konfime' : 'Yon erè rive'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {status === 'expired' 
              ? 'Sesyon peman ou ekspire. Tanpri eseye ankò.' 
              : status === 'timeout'
              ? 'Peman ou ap trete toujou. Tcheke paj kòmand ou yo nan kèk minit.'
              : 'Nou pa kapab verifye peman ou. Tanpri kontakte sipò.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cart">
              <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8">
                Retounen nan Panye
              </Button>
            </Link>
            <Link to="/orders">
              <Button variant="outline" className="px-8">
                Wè Kòmand yo
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
